"use client";

import type { FormStep } from "./form-step";
import type { LeadFormMachineInitialContext } from "./lead-form-machine";

import { useMachine } from "@xstate/react";
import { useCallback, useMemo, useState } from "react";
import { createLeadFormMachine } from "./lead-form-machine";
import { getStepperState } from "./use-stepper-state";

// Stable fallback objects prevent memoization breaks in consumers (useFormInputs etc.)
// when context fields are undefined. Inline `?? {}` / `?? { ... }` would produce a
// new object reference on every render, defeating useMemo dependency equality checks.
const EMPTY_PERSISTED_FIELDS: Record<string, string> = {};
const EMPTY_POSTAL_LOCATION = { location: "", postalCode: "" };

export interface UseLeadFormMachineReturn {
  onValidationError: (errors: Record<string, string[]>) => void;
  onContinue: () => void;
  onFieldChange: (name: string, value: string) => void;
  onPostalLocationChange: (values: {
    postalCode: string;
    location: string;
  }) => void;
  errors: Record<string, string[]>;
  persistedFieldValues: Record<string, string>;
  postalLocationValues: { postalCode: string; location: string };
  currentStep: FormStep;
  currentStepIndex: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  hasAutoAdvanceInput: boolean;
  isHeroMode: boolean;
  stepperProps: {
    currentStep: number;
    totalSteps: number;
    handleBack: () => void;
    showBackButton: boolean;
    showStepper: boolean;
  };
  /**
   * Drives the machine to the given step index via repeated NEXT/BACK sends,
   * so `skipOnBack` interstitials are resolved the same way as a normal
   * BACK/NEXT. Used to realign the machine with browser history navigation
   * (popstate), which reports a target index rather than a direction.
   */
  syncToStepIndex: (targetStepIndex: number) => void;
}

export function useLeadFormMachine(
  steps: FormStep[],
  options?: { initialContext?: LeadFormMachineInitialContext },
): UseLeadFormMachineReturn {
  // useState lazy initializer runs exactly once on mount. Both `steps` and
  // `initialContext` are intentionally frozen at construction time:
  // - steps: the form definition is static for the lifetime of the component
  // - initialContext: used only for postal prefill from office data; recreating
  //   the machine when parent office props change would wipe in-progress form state
  const [machine] = useState(() =>
    createLeadFormMachine(
      steps.map((step) => ({
        id: step.id,
        skipOnBack:
          step._type === "interstitialStep" && step.autoAdvance === true,
      })),
      options?.initialContext,
    ),
  );
  const [snapshot, send, actorRef] = useMachine(machine);

  const activeStepId = String(snapshot.value);
  const currentStepIndex = steps.findIndex((s) => s.id === activeStepId);
  const resolvedStepIndex = currentStepIndex === -1 ? 0 : currentStepIndex;
  const currentStep = steps[resolvedStepIndex];
  const totalSteps = steps.length;
  const isFirstStep = resolvedStepIndex === 0;
  const isLastStep = resolvedStepIndex === totalSteps - 1;

  const hasAutoAdvanceInput =
    currentStep._type === "step" &&
    (currentStep.inputs?.some(
      (input) => input._type === "radioInput" && input.autoAdvance,
    ) ??
      false);

  const isHeroMode = currentStep.infoPanel?.mode === "hero";

  const handleBack = useCallback(() => send({ type: "BACK" }), [send]);
  const onContinue = useCallback(() => send({ type: "NEXT" }), [send]);
  const onValidationError = useCallback(
    (errors: Record<string, string[]>) =>
      send({ errors, type: "VALIDATION_ERROR" }),
    [send],
  );
  const onFieldChange = useCallback(
    (name: string, value: string) =>
      send({ name, type: "FIELD_CHANGE", value }),
    [send],
  );
  const onPostalLocationChange = useCallback(
    (values: { postalCode: string; location: string }) =>
      send({
        location: values.location,
        postalCode: values.postalCode,
        type: "POSTAL_LOCATION_CHANGE",
      }),
    [send],
  );

  const syncToStepIndex = useCallback(
    (targetStepIndex: number) => {
      const boundedTargetIndex = Math.min(
        Math.max(targetStepIndex, 0),
        steps.length - 1,
      );

      // Bounded by steps.length: each send moves at most one state node, and a
      // linear machine can never be more than steps.length transitions away.
      for (let i = 0; i < steps.length; i++) {
        const activeId = String(actorRef.getSnapshot().value);
        const activeIndex = steps.findIndex((step) => step.id === activeId);

        if (activeIndex === boundedTargetIndex) {
          return;
        }

        send({ type: activeIndex < boundedTargetIndex ? "NEXT" : "BACK" });
      }
    },
    [actorRef, send, steps],
  );

  const stepperState = useMemo(
    () => getStepperState(steps, activeStepId),
    [steps, activeStepId],
  );

  const stepperProps = useMemo(
    () => ({
      currentStep: stepperState.currentStep,
      handleBack,
      showBackButton: !isFirstStep,
      showStepper: stepperState.showStepper,
      totalSteps: stepperState.totalSteps,
    }),
    [stepperState, handleBack, isFirstStep],
  );

  return {
    currentStep,
    currentStepIndex: resolvedStepIndex,
    errors: snapshot.context.errors ?? {},
    hasAutoAdvanceInput,
    isFirstStep,
    isHeroMode,
    isLastStep,
    onContinue,
    onFieldChange,
    onPostalLocationChange,
    onValidationError,
    persistedFieldValues:
      snapshot.context.persistedFieldValues ?? EMPTY_PERSISTED_FIELDS,
    postalLocationValues:
      snapshot.context.postalLocationValues ?? EMPTY_POSTAL_LOCATION,
    stepperProps,
    syncToStepIndex,
    totalSteps,
  };
}
